/**
 * <ec-header>
 *
 * Root header component. Responsibilities:
 *  - Sticky scroll-up: hide on scroll-down past threshold, reveal on scroll-up
 *  - Trigger orchestration: opens drawer / search overlay; relays cart click
 *    via cancelable `ec:cart-drawer:open`
 *  - Keeps --header-height fresh on resize
 *  - Closes child overlays on Escape if no other handler claimed it
 *  - Keeps the cart badge in sync with the live cart (cart:update events
 *    from anywhere on the page; sessionStorage rehydration on bfcache)
 *
 * The element ALSO carries id="header-component" so the inline IIFE in
 * layout/theme.liquid (which queries '#header-component') keeps working.
 */

import { ThemeEvents } from '@theme/events';

const SCROLL_THRESHOLD = 200;
const SCROLL_JITTER = 4;

class EcHeader extends HTMLElement {
  static observedAttributes = ['sticky', 'data-transparent'];

  #lastScrollY = 0;
  #rafId = null;
  #onScroll = null;
  #onClick = null;
  #resizeObserver = null;
  #drawer = null;
  #search = null;
  #megaMenu = null;
  #onMegaOpen = null;
  #onSearchOpen = null;
  #onDrawerOpen = null;
  #onCartUpdate = null;
  #onPageshow = null;

  connectedCallback() {
    this.#drawer = this.querySelector('ec-mobile-drawer');
    this.#search = this.querySelector('ec-search-overlay');
    this.#megaMenu = this.querySelector('ec-mega-menu');

    this.#bindClickDelegation();
    this.#bindScrollHandler();
    this.#bindResizeObserver();
    this.#bindOverlayState();
    this.#bindCartBadgeSync();
  }

  disconnectedCallback() {
    if (this.#onScroll) document.removeEventListener('scroll', this.#onScroll);
    if (this.#onClick) this.removeEventListener('click', this.#onClick, true);
    if (this.#rafId !== null) cancelAnimationFrame(this.#rafId);
    this.#resizeObserver?.disconnect();
    if (this.#onMegaOpen) {
      this.removeEventListener('ec:mega:open', this.#onMegaOpen);
      this.removeEventListener('ec:mega:close', this.#onMegaOpen);
    }
    if (this.#onSearchOpen) {
      this.removeEventListener('ec:search:open', this.#onSearchOpen);
      this.removeEventListener('ec:search:close', this.#onSearchOpen);
    }
    if (this.#onDrawerOpen) {
      this.removeEventListener('ec:drawer:open', this.#onDrawerOpen);
      this.removeEventListener('ec:drawer:close', this.#onDrawerOpen);
    }
    if (this.#onCartUpdate) {
      document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    }
    if (this.#onPageshow) {
      window.removeEventListener('pageshow', this.#onPageshow);
    }
  }

  attributeChangedCallback(name) {
    if (name === 'sticky') this.#bindScrollHandler();
  }

  #bindClickDelegation() {
    this.#onClick = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const drawerTrigger = target.closest('[data-ec-drawer-trigger]');
      if (drawerTrigger) {
        event.preventDefault();
        if (this.#drawer && typeof this.#drawer.open === 'function') {
          this.#drawer.open({ previousFocus: drawerTrigger });
        }
        return;
      }

      const searchTrigger = target.closest('[data-ec-search-trigger]');
      if (searchTrigger) {
        event.preventDefault();
        if (this.#search && typeof this.#search.open === 'function') {
          this.#search.open({ previousFocus: searchTrigger });
        }
        return;
      }

      const cartTrigger = target.closest('[data-ec-cart-trigger]');
      if (cartTrigger) {
        // <ec-cart-drawer> calls preventDefault on this event when it
        // accepts the open; the link falls back to /cart navigation when
        // the drawer is absent or its module hasn't loaded yet.
        const detail = { source: cartTrigger };
        const evt = new CustomEvent('ec:cart-drawer:open', {
          bubbles: true,
          cancelable: true,
          detail,
        });
        const accepted = !this.dispatchEvent(evt);
        if (accepted) event.preventDefault();
      }
    };
    this.addEventListener('click', this.#onClick, true);
  }

  #bindScrollHandler() {
    if (this.#onScroll) {
      document.removeEventListener('scroll', this.#onScroll);
      this.#onScroll = null;
    }
    if (this.getAttribute('sticky') !== 'scroll-up') {
      this.dataset.scrollDirection = 'none';
      return;
    }

    this.#lastScrollY = window.scrollY;
    this.#onScroll = () => {
      if (this.#rafId !== null) return;
      this.#rafId = requestAnimationFrame(() => {
        this.#rafId = null;
        this.#updateScrollState();
      });
    };
    document.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#updateScrollState();
  }

  #updateScrollState() {
    this.#updateAccountOffset();
    const y = window.scrollY;
    const dy = y - this.#lastScrollY;
    if (Math.abs(dy) < SCROLL_JITTER) {
      this.#lastScrollY = y;
      return;
    }

    let direction;
    if (y < SCROLL_THRESHOLD) {
      direction = 'none';
    } else if (this.#hasOpenOverlay()) {
      direction = 'none';
    } else {
      direction = dy > 0 ? 'down' : 'up';
    }

    this.dataset.scrollDirection = direction;
    this.dataset.scrollY = y === 0 ? 'top' : 'scrolled';
    this.#lastScrollY = y;
  }

  #hasOpenOverlay() {
    if (this.#drawer && !this.#drawer.hidden) return true;
    if (this.#search && !this.#search.hidden) return true;
    if (this.#megaMenu?.querySelector('.ec-mega-menu__panel:not([hidden])')) return true;
    return false;
  }

  #bindResizeObserver() {
    if (typeof ResizeObserver === 'undefined') return;
    this.#resizeObserver = new ResizeObserver(() => {
      const h = this.offsetHeight;
      document.body.style.setProperty('--header-height', `${h}px`);
      this.#updateAccountOffset();
    });
    this.#resizeObserver.observe(this);
  }

  // The native <shopify-account> dialog is viewport-fixed; offset its top to
  // the header's bottom edge so it drops below the icons instead of pinning
  // to the top. Tracks the announcement bar height and sticky scroll state.
  #updateAccountOffset() {
    const bottom = Math.max(0, Math.round(this.getBoundingClientRect().bottom));
    document.body.style.setProperty('--ec-account-dialog-top', `${bottom}px`);
  }

  #bindOverlayState() {
    const reassert = () => this.#updateScrollState();
    this.#onMegaOpen = reassert;
    this.#onSearchOpen = reassert;
    this.#onDrawerOpen = (event) => {
      reassert();
      this.toggleAttribute('data-ec-drawer-open', event.type === 'ec:drawer:open');
    };
    this.addEventListener('ec:mega:open', this.#onMegaOpen);
    this.addEventListener('ec:mega:close', this.#onMegaOpen);
    this.addEventListener('ec:search:open', this.#onSearchOpen);
    this.addEventListener('ec:search:close', this.#onSearchOpen);
    this.addEventListener('ec:drawer:open', this.#onDrawerOpen);
    this.addEventListener('ec:drawer:close', this.#onDrawerOpen);
  }

  #bindCartBadgeSync() {
    this.#onCartUpdate = (event) => {
      const count = this.#extractCount(event);
      if (count === null) {
        // Resource was not supplied (e.g. add-to-cart returns the line item
        // alone). Refetch /cart.js once to get a fresh snapshot.
        this.#refreshFromCartJs();
        return;
      }
      this.#renderBadge(count);
    };

    this.#onPageshow = (event) => {
      // bfcache restore — the Liquid render is frozen, so the SSR count may
      // be stale (cart changed in another tab or after the freeze). One
      // extra /cart.js call here is cheaper than guessing from storage.
      if (event.persisted) this.#refreshFromCartJs();
    };

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    window.addEventListener('pageshow', this.#onPageshow);
  }

  #extractCount(event) {
    const detail = event?.detail;
    if (detail?.resource?.item_count != null) return Number(detail.resource.item_count);
    if (typeof detail?.data?.itemCount === 'number') return detail.data.itemCount;
    return null;
  }

  async #refreshFromCartJs() {
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();
      const count = Number(cart.item_count || 0);
      this.#renderBadge(count);
    } catch (err) {
      console.error('[ec-header] cart count refresh failed', err);
    }
  }

  #renderBadge(count) {
    const badges = this.querySelectorAll('[data-ec-cart-count]');
    if (!badges.length) return;
    for (const badge of badges) {
      badge.textContent = String(count);
      badge.dataset.count = String(count);
      badge.toggleAttribute('hidden', count === 0);
      badge.classList.remove('is-pulse');
      void badge.offsetWidth;
      badge.classList.add('is-pulse');
    }
  }
}

if (!customElements.get('ec-header')) {
  customElements.define('ec-header', EcHeader);
}
